/**
 * sceneIntro — Lauren Brock intro reel. 5 seconds, 1080×1920 portrait.
 *
 * Simplified version: bg, animated "LAUREN" name, tagline reveal,
 * magenta underline, NSPP mark, fade-to-black. No particles or orbits
 * (those broke rendering earlier). Plain rects + text only.
 */

import {
  type Scene,
  createScene,
  makeRect,
  makeText,
  addNode,
} from "../sceneGraph";

const NAME = "LAUREN";
const TAG = "TATTOO MODEL  ·  UK  ·  BOOK DIRECT";

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

export function sceneIntro(w: number, h: number): Scene {
  const scene = createScene();
  const T0 = performance.now() / 1000;
  const T = () => performance.now() / 1000 - T0;

  // ----- Background -----
  addNode(scene, makeRect({
    x: 0, y: 0, w, h,
    fill: { r: 0.02, g: 0.02, b: 0.04, a: 1 },
  }));

  // ----- Hairlines top + bottom (draw outward from center 1-2s) -----
  for (let edge = 0; edge < 2; edge++) {
    const yLine = edge === 0 ? h * 0.12 : h * 0.88;
    addNode(scene, makeRect({
      x: w / 2, y: yLine, w: 0, h: 1,
      fill: { r: 1, g: 1, b: 1, a: 1 },
      update: (n) => {
        const t = T();
        const halfTarget = w * 0.30;
        let halfWidth: number, alpha: number;
        if (t < 1.0) { halfWidth = 0; alpha = 0; }
        else if (t < 2.0) {
          halfWidth = halfTarget * easeOutCubic((t - 1.0));
          alpha = 0.20;
        } else if (t < 3.5) { halfWidth = halfTarget; alpha = 0.20; }
        else if (t < 5.0) { halfWidth = halfTarget; alpha = 0.20 * (1 - (t - 3.5) / 1.5); }
        else { halfWidth = halfTarget; alpha = 0; }
        n.w = halfWidth * 2;
        n.x = w / 2 - halfWidth;
        n.fill = { r: 1, g: 1, b: 1, a: alpha };
      },
    }));
  }

  // ----- Name "LAUREN" (fade + scale-pop) -----
  const nameH = 110;
  const nameW = w * 0.90;
  const nameX = (w - nameW) / 2;
  const nameY = h * 0.42 - nameH / 2;
  addNode(scene, makeText({
    x: nameX, y: nameY, w: nameW, h: nameH,
    text: NAME,
    fontSize: 104, fontWeight: 800,
    textAlign: "center", letterSpacing: 14,
    textColor: { r: 1, g: 1, b: 1, a: 1 },
    update: (n) => {
      const t = T();
      let a: number;
      if (t < 0.2) a = 0;
      else if (t < 1.4) a = easeOutCubic((t - 0.2) / 1.2);
      else if (t < 3.8) a = 1;
      else if (t < 4.5) a = 1 - (t - 3.8) / 0.7;
      else a = 0;
      let s = 1;
      if (t < 1.4) {
        const k = clamp((t - 0.2) / 1.2, 0, 1);
        s = lerp(0.85, 1.0, easeOutCubic(k));
      } else if (t < 3.8) {
        s = 1 + Math.sin(t * Math.PI) * 0.02;
      }
      n.textColor = { r: 1, g: 1, b: 1, a };
      n.scaleX = s; n.scaleY = s;
      n.x = nameX + nameW / 2 - (nameW * s) / 2;
      n.y = nameY + nameH / 2 - (nameH * s) / 2;
    },
  }));

  // ----- Tagline (fade in 1.5-2.5s) -----
  const tagH = 36;
  const tagW = w * 0.86;
  const tagX = (w - tagW) / 2;
  const tagY = h * 0.55;
  addNode(scene, makeText({
    x: tagX, y: tagY, w: tagW, h: tagH,
    text: TAG,
    fontSize: 22, fontWeight: 500,
    textAlign: "center", letterSpacing: 4,
    textColor: { r: 0.96, g: 0.92, b: 1, a: 1 },
    update: (n) => {
      const t = T();
      let a: number;
      if (t < 1.5) a = 0;
      else if (t < 2.5) a = easeOutCubic((t - 1.5) / 1);
      else if (t < 3.8) a = 1;
      else if (t < 4.5) a = 1 - (t - 3.8) / 0.7;
      else a = 0;
      n.textColor = { r: 0.96, g: 0.92, b: 1, a };
    },
  }));

  // ----- Magenta underline (draws 2-3s) -----
  const underlineY = tagY + tagH + 14;
  addNode(scene, makeRect({
    x: tagX, y: underlineY, w: 0, h: 2,
    fill: { r: 0.96, g: 0.30, b: 0.55, a: 1 },
    update: (n) => {
      const t = T();
      let width = 0;
      let alpha = 0;
      if (t < 2.0) { width = 0; alpha = 0; }
      else if (t < 3.0) {
        width = tagW * easeOutCubic((t - 2.0) / 1);
        alpha = 1;
      } else if (t < 3.8) { width = tagW; alpha = 1; }
      else if (t < 4.5) { width = tagW; alpha = 1 - (t - 3.8) / 0.7; }
      else { width = tagW; alpha = 0; }
      n.w = width;
      n.fill = { r: 0.96, g: 0.30, b: 0.55, a: alpha };
    },
  }));

  // ----- NSPP wordmark bottom-right -----
  addNode(scene, makeText({
    x: w * 0.62, y: h * 0.93, w: w * 0.32, h: 20,
    text: "NSPP",
    fontSize: 12, fontWeight: 700,
    textAlign: "right", letterSpacing: 3,
    textColor: { r: 0.6, g: 0.6, b: 0.65, a: 1 },
    update: (n) => {
      const t = T();
      let a: number;
      if (t < 3.5) a = 0;
      else if (t < 4.0) a = (t - 3.5) / 0.5;
      else if (t < 4.5) a = 1;
      else if (t < 5.0) a = 1 - (t - 4.5) / 0.5;
      else a = 0;
      n.textColor = { r: 0.6, g: 0.6, b: 0.65, a: a * 0.8 };
    },
  }));

  // ----- Final fade-to-black -----
  addNode(scene, makeRect({
    x: 0, y: 0, w, h,
    fill: { r: 0.02, g: 0.02, b: 0.04, a: 0 },
    update: (n) => {
      const t = T();
      let a = 0;
      if (t > 4.5) a = clamp((t - 4.5) / 0.5, 0, 1);
      n.fill = { r: 0.02, g: 0.02, b: 0.04, a };
    },
  }));

  return scene;
}

export const INTRO_DURATION_SEC = 5;
export const INTRO_WIDTH = 1080;
export const INTRO_HEIGHT = 1920;